import { PrinterIcon } from "@heroicons/react/24/outline";
import { EnvelopeIcon, HomeIcon, PhoneIcon } from "@heroicons/react/24/solid";
import dayjs from "dayjs";
import dynamic from "next/dynamic";
import { Fragment, useEffect, useState } from "react";
import shortUrl from "../vars/shortUrl";
import Button from "./Button";

const QRRender = dynamic(() => import("@/components/QRRender"), {
  ssr: false,
});

const CVPreviewComponent = () => {
  const [data, setData] = useState<any>(null);
  const [printOpen, setPrintOpen] = useState(false);

  const onReceive = (e) => {
    const _data = e.data;
    if (_data.from === "microsite_cv" && _data.action === "SYNC") {
      setData(_data.data);
      if (!printOpen) {
        setPrintOpen(true);
        setTimeout(() => {
          window.print();
        }, 500);
      }
    }
  };

  useEffect(() => {
    window?.opener?.postMessage({ from: "microsite_cv", action: "READY" }, "*");
    window.addEventListener("message", onReceive);
    return () => {
      window.removeEventListener("message", onReceive);
    };
  }, []);

  if (data === null) {
    return <Fragment />;
  }

  return (
    <Fragment>
      <div className="print:hidden top-0 left-0 fixed flex justify-end items-center bg-white p-2 border-b w-full">
        <div className="flex-1 pl-2 font-semibold">Preview</div>
        <Button
          size="sm"
          variant="secondary"
          className="flex text-sm"
          onClick={() => {
            window.print();
          }}
        >
          <PrinterIcon className="mr-2 w-4" />
          Print Document
        </Button>
      </div>
      <div className="print:hidden mt-12" />
      <div className="flex flex-col bg-white p-3 min-h-screen">
        <style>{`
      @page { size: A4 }
      body{
        -webkit-print-color-adjust:exact !important;
        print-color-adjust:exact !important;
      }
      `}</style>
        <div className="flex-1">
          <h1 className="font-bold text-lg">{data.fullName}</h1>
          <p className="mb-2 font-semibold text-gray-600 text-sm">
            {data.description}
          </p>
          <p className="flex gap-4 text-xs">
            {data.email ? (
              <span>
                <EnvelopeIcon className="inline-block w-3" /> {data.email}
              </span>
            ) : (
              <Fragment />
            )}
            {data.phone ? (
              <span>
                <PhoneIcon className="inline-block -mt-1 w-3" /> {data.phone}
              </span>
            ) : (
              <Fragment />
            )}
          </p>
          {data.address && (
            <p className="py-2 text-xs">
              <HomeIcon className="inline-block -mt-1 w-3" /> {data.address}
            </p>
          )}
          <hr />
          <p className="mt-2 mb-2 text-xs">{data.summary}</p>
          <hr />

          {data?.experiences?.length > 0 && (
            <Fragment>
              <h2 className="mt-4 font-semibold text-lg">Working Experience</h2>

              <ul>
                {data.experiences?.map((exp, i) => (
                  <li key={i} className="py-3 border-b last:border-b-0">
                    <div className="flex">
                      <div className="flex-1 shrink-0">
                        <p className="font-bold">{exp.name}</p>
                        <p className="text-xs">{exp.position}</p>
                      </div>
                      <div className="mt-1 text-xs text-right shrink-0">
                        <p>
                          {dayjs(exp.startDate).format("DD MMMM YYYY")} &mdash;{" "}
                          {exp.currentlyHere
                            ? "Present"
                            : dayjs(exp.endDate).format("DD MMMM YYYY")}
                        </p>
                        {exp.location ? <p>{exp.location}</p> : ""}
                      </div>
                    </div>
                    <p className="mt-1 text-xs">{exp.description}</p>
                  </li>
                ))}
              </ul>

              <hr className="mx-auto my-6 border-b-2 w-10" />
            </Fragment>
          )}

          {data?.educations?.length > 0 && (
            <Fragment>
              <h2 className="mt-4 font-semibold text-lg">Education</h2>

              <ul>
                {data.educations?.map((exp, i) => (
                  <li key={i} className="py-3 border-b last:border-b-0">
                    <div className="flex">
                      <div className="flex-1 shrink-0">
                        <p className="font-bold text-sm">
                          {exp.name} {exp.gpa ? `(GPA ${exp.gpa})` : ""}
                        </p>
                        <p className="text-sm">{exp.position}</p>
                      </div>
                      <div className="mt-1 text-xs text-right shrink-0">
                        <p>
                          {dayjs(exp.startDate).format("DD MMMM YYYY")} &mdash;{" "}
                          {exp.currentlyHere
                            ? "Present"
                            : dayjs(exp.endDate).format("DD MMMM YYYY")}
                        </p>
                        {exp.location ? <p>{exp.location}</p> : ""}
                      </div>
                    </div>
                    <p className="mt-1 text-xs">{exp.description}</p>
                  </li>
                ))}
              </ul>

              <hr className="mx-auto my-6 border-b-2 w-10" />
            </Fragment>
          )}

          {data?.skills?.length > 0 && (
            <Fragment>
              <h2 className="mt-4 font-semibold text-lg">Skill</h2>

              <ul>
                {data.skills?.map((exp, i) => (
                  <li key={i} className="py-3 border-b last:border-b-0">
                    <p className="font-bold text-sm">{exp.title}</p>
                    <p className="text-xs">{exp.description}</p>
                  </li>
                ))}
              </ul>
            </Fragment>
          )}
        </div>

        <div className="flex justify-end items-end p-1">
          <div className="flex-1 mr-10 pt-2 border-t text-gray-500 text-xs">
            Curriculum Vitae generated with s.id
          </div>
          <div className="flex flex-col items-end">
            <div className="w-[100px]">
              <QRRender
                url={`${shortUrl}/${data.slug}?s=qr`}
                id={"1"}
                noShadow
                data={{ active: true }}
                isLoading={false}
              />
            </div>
            <p className="min-w-[100px] text-xs text-center">
              s.id/{data.slug}
            </p>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default CVPreviewComponent;
